import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { ArrowsClockwise } from "@phosphor-icons/react";

import chartEmptyIllustration from "assets/chart-states/chart_empty.png";
import chartErrorIllustration from "assets/chart-states/chart_error.png";

// ---------------------------------------------------------------------------
// Base card
// ---------------------------------------------------------------------------

type ChartStateSize = "default" | "compact";

interface ChartStateCardProps {
  /** Illustration shown above the text block. */
  image: string;
  /** Headline text. */
  title: string;
  /** Supporting copy below the title. */
  description?: string;
  /** Action element rendered below the text block (typically a Button). */
  action?: React.ReactNode;
  /** "compact" shrinks the illustration and type for small slots (e.g. GoalCard). */
  size?: ChartStateSize;
  /** Min height of the card. */
  minHeight?: number | string;
}

const ILLUSTRATION_SIZE: Record<ChartStateSize, number> = {
  default: 200,
  compact: 96,
};

/**
 * Bordered placeholder card for chart areas — used while a chart has no data
 * to plot or failed to load. Centered illustration + title + supporting copy
 */
const ChartStateCard: React.FC<ChartStateCardProps> = ({
  image,
  title,
  description,
  action,
  size = "default",
  minHeight,
}) => (
  <Stack
    component="section"
    alignItems="center"
    justifyContent="center"
    aria-label={title}
    sx={{
      width: "100%",
      minHeight,
      p: size === "compact" ? 4 : 7, // 24px on default per design spec
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      // 12px is not in theme.radius tokens (max is ex-large = 8px) — using design spec value
      borderRadius: "12px",
      overflow: "hidden",
    }}
  >
    <Stack direction="column" alignItems="center">
      <Box
        component="img"
        src={image}
        alt=""
        aria-hidden
        sx={{
          width: ILLUSTRATION_SIZE[size],
          height: ILLUSTRATION_SIZE[size],
          objectFit: "cover",
          pointerEvents: "none",
        }}
      />

      <Stack direction="column" alignItems="center" gap={2}>
        <Stack
          direction="column"
          alignItems="center"
          sx={{ textAlign: "center", wordBreak: "break-word" }}
        >
          <Typography
            component="p"
            sx={{
              fontWeight: 500,
              fontSize: size === "compact" ? "14px" : "16px",
              lineHeight: 1.75,
              letterSpacing: "0.15px",
              color: "text.primary",
            }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography
              component="p"
              variant={size === "compact" ? "caption" : "body2"}
              sx={{ color: "text.secondary", letterSpacing: "0.17px" }}
            >
              {description}
            </Typography>
          ) : null}
        </Stack>

        {action}
      </Stack>
    </Stack>
  </Stack>
);

// ---------------------------------------------------------------------------
// Public variants
// ---------------------------------------------------------------------------

export interface ChartEmptyStateProps {
  /** Headline override — defaults to the Figma empty-state copy. */
  title?: string;
  /** Supporting copy override — defaults to the target-scoped Figma copy. */
  description?: string;
  /** "compact" shrinks the illustration and type for small slots (e.g. GoalCard). */
  size?: ChartStateSize;
  /** Min height of the card. */
  minHeight?: number | string;
}

/**
 * Empty placeholder for a chart area — shown while the target/goal has no
 * session data to plot yet.
 */
export const ChartEmptyState: React.FC<ChartEmptyStateProps> = ({
  title = "Nothing in the chart yet.",
  description = "Trial data will chart here once sessions are run on this target.",
  size,
  minHeight,
}) => (
  <ChartStateCard
    image={chartEmptyIllustration}
    title={title}
    description={description}
    size={size}
    minHeight={minHeight}
  />
);

export interface ChartErrorStateProps {
  /** Called when the user clicks Refresh — typically a React Query refetch. */
  onRetry: () => void;
  /** Headline override — defaults to the Figma error-state copy. */
  title?: string;
  /** Supporting copy override. */
  description?: string;
  /** "compact" shrinks the illustration and type for small slots (e.g. GoalCard). */
  size?: ChartStateSize;
  /** Min height of the card. */
  minHeight?: number | string;
}

/**
 * Error placeholder for a chart area — shown when chart data failed to load,
 * with a Refresh action to retry the fetch.
 */
export const ChartErrorState: React.FC<ChartErrorStateProps> = ({
  onRetry,
  title = "Couldn't load chart.",
  description = "Refresh to try again.",
  size,
  minHeight,
}) => (
  <ChartStateCard
    image={chartErrorIllustration}
    title={title}
    description={description}
    size={size}
    minHeight={minHeight}
    action={
      <Button
        variant="contained"
        color="secondary"
        size="small"
        startIcon={<ArrowsClockwise size={16} weight="regular" />}
        onClick={onRetry}
      >
        Refresh
      </Button>
    }
  />
);

export default ChartStateCard;
